import { Clock, Home, ShoppingCart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LabTest } from "@/services/data";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Props {
  test: LabTest;
}

export default function TestCard({ test }: Props) {
  const { addTest, hasItem } = useCart();
  const navigate = useNavigate();
  const inCart = hasItem(test.id);

  const handleAdd = () => {
    addTest(test);
    toast.success(`${test.name} added to cart`);
  };

  const discount = Math.round(((test.price - test.discountedPrice) / test.price) * 100);

  return (
    <div className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-all group flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1">
          <Badge variant="secondary" className="text-xs mb-2">{test.category}</Badge>
          <h3 className="font-semibold text-sm text-foreground leading-snug group-hover:text-primary transition-colors">
            {test.name}
          </h3>
        </div>
        {test.popular && (
          <Badge className="bg-orange-100 text-orange-700 border-0 text-xs shrink-0">Popular</Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{test.description}</p>

      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{test.turnaround}</span>
        {test.homeCollection && <span className="flex items-center gap-1 text-green-600"><Home className="h-3 w-3" />Home</span>}
        {test.fasting && <span className="text-amber-600">Fasting</span>}
      </div>

      <div className="mt-auto">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg font-bold text-foreground">₹{test.discountedPrice}</span>
          <span className="text-xs text-muted-foreground line-through">₹{test.price}</span>
          <Badge className="bg-green-100 text-green-700 border-0 text-xs">{discount}% off</Badge>
        </div>

        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 text-xs h-8"
            variant={inCart ? "secondary" : "default"}
            onClick={inCart ? () => navigate("/booking") : handleAdd}
          >
            {inCart ? (
              <><Check className="h-3 w-3 mr-1" />Added</>
            ) : (
              <><ShoppingCart className="h-3 w-3 mr-1" />Add to Cart</>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-8"
            onClick={() => { if (!inCart) addTest(test); navigate("/booking"); }}
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
}
